import { useState, useCallback } from 'react';
import { getAntiForgeryToken } from '@/utils/getAntiForgeryToken';
import { Endpoint } from '@/constants';

interface ApplePayDeliveryOption {
  id: string;
  identifier: string;
  label: string;
  detail: string;
  amount: string;
  isDefault: boolean;
}

interface ApplePayDeliveryOptionsResponse {
  options: ApplePayDeliveryOption[];
  isDeliverySupported: boolean;
  unsupportedMessage?: string;
}

interface DeliveryAddress {
  firstName: string;
  lastName: string;
  line1: string;
  line2?: string;
  city: string;
  county?: string;
  postalCode: string;
  countryCode: string;
  phone?: string;
}

// Cache for delivery options by country
const deliveryOptionsCache = new Map<string, ApplePayDeliveryOptionsResponse>();

export const useApplePayDeliveryOptions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateDeliveryAddress = useCallback(async (address: DeliveryAddress): Promise<boolean> => {
    try {
      const response = await fetch(Endpoint.UpdateDeliveryAddress, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'RequestVerificationToken': getAntiForgeryToken()
        },
        body: JSON.stringify(address)
      });

      if (!response.ok) {
        throw new Error('Failed to update delivery address');
      }

      const result = await response.json();
      return result.isSuccess || result.success || true;
    } catch (err) {
      console.error('Error updating delivery address:', err);
      setError('Failed to update delivery address');
      return false;
    }
  }, []);

  const fetchDeliveryOptions = useCallback(async (
    countryCode: string, 
    isBuyNow?: boolean, 
    productCode?: string
  ): Promise<ApplePayDeliveryOptionsResponse> => {

    setLoading(true);
    setError(null);

    try {
      // Build URL with optional parameters
      let fetchUrl = `${Endpoint.GetShippingOptions}?countryCode=${countryCode}`;
      
      if (isBuyNow === true) {
        fetchUrl += `&isBuyNow=true`;
        if (productCode) {
          fetchUrl += `&productCode=${encodeURIComponent(productCode)}`;
          // Generate cart name for Buy Now mode
          const buyNowCartName = `ApplePay_BuyNow_${productCode}`;
          fetchUrl += `&buyNowCartName=${encodeURIComponent(buyNowCartName)}`;
        }
      }

      const fetchResponse = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'RequestVerificationToken': getAntiForgeryToken()
        }
      });

      if (!fetchResponse.ok) {
        throw new Error(`Failed to fetch delivery options: ${fetchResponse.statusText}`);
      }

      const data = await fetchResponse.json();
      
      // Check if delivery is supported for this country
      if (!data.isSuccess || !data.data || data.data.length === 0) {
        const unsupportedResponse: ApplePayDeliveryOptionsResponse = {
          options: [],
          isDeliverySupported: false,
          unsupportedMessage: `We currently do not deliver to ${countryCode}`
        };
        deliveryOptionsCache.set(countryCode, unsupportedResponse);
        return unsupportedResponse;
      }

      // Convert backend format to Apple Pay format
      const options: ApplePayDeliveryOption[] = data.data.map((option: any) => ({
        id: option.type || option.id,
        identifier: option.type || option.id,
        label: option.title,
        detail: option.description,
        amount: option.priceMoney.amount.toString(),
        isDefault: option.isDefault || false
      }));

      const deliveryResponse: ApplePayDeliveryOptionsResponse = {
        options,
        isDeliverySupported: true
      };
      
      return deliveryResponse;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch delivery options');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    updateDeliveryAddress,
    fetchDeliveryOptions
  };
};